import React, { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuthStore } from "../../../../store/storeAuth";
import { useDarkMode } from "../../../../hooks/useDarkMode";
import { useSidebarData } from "../../../../hooks/useSidebarData";
import { vectorQueries } from "../../../../queries/vector";
import { useObjectStore } from "../../../../store/storeObjects";
import {
  useNotificationStore,
  NotificationType,
} from "../../../../store/storeNotifications";
import ObjectModal from "../../../Layout/LayoutComponents/FluxObjects/ObjectModal";
import { VectorMemoryModal } from "../../../UI/VectorMemoryModal";
import CreateFormModal from "../../../Forms/CreateFormModal";
import AddToLibraryModal from "../../../Library/AddToLibraryModal";
import { FolderService } from "../../../../service/FolderService";
import { Folder, Flows } from "../../../../util/Types/Folder";
import { UserObject } from "../../../../util/Types/User";
import { Space } from "../../../../util/Types/Sidebar";
import { Namespace } from "../../../../types/namespace";
import { isFluxbotFlow, isFormFlow, parseSpaceIdFromFolderId } from "../utils";
import {
  AgentsSidebarContext,
  AgentsSidebarContextValue,
  FluxObject,
  useAgentsSidebarContext,
} from "./context";
import { useExtendedSidebarContext } from "../ExtendedSidebar";
import { Section } from "./Section";
import { FolderItem } from "./FolderItem";
import { FluxBotItem } from "./FluxBotItem";
import { Item } from "./Item";
import { ActionMenu } from "./ActionMenu";
import { BoundingBox, ChatCircleText, FileText, HardDrives, Stack } from "@phosphor-icons/react";
import TemplateSystemModal from "../../../TemplateSystemModal/TemplateSystemModal";
import AddUsersModal from "../../../Spaces/AddUsersModal";
import CopySpaceIDModal from "../../../Layout/LayoutComponents/Spaces/CopySpaceIDModal";
import UsersAddedModal from "../../../Spaces/UsersAddedModal";
import CreateFolderModal from "../../LayoutComponents/PrivateFlows/FolderModal/FolderModal";
import { CreateItemModal } from "../CreateItemModal";
import { createMainFolder } from "./folderStructure";
import { useAgentsSidebarHandlers, useAgentsSidebarData } from "./hooks";

const RootAgentItem: React.FC<{
  agent: Flows;
  folderId: string;
  forceFluxbotIcon?: boolean;
  onRename?: (flow: Flows) => void;
  onDelete?: (flow: Flows) => void;
  onShare?: (flow: Flows) => void;
  onOpenInNewTab?: (flow: Flows) => void;
  onCreateInterface?: (flow: Flows, folderId: string) => void;
  onAddToLibrary?: (flow: Flows, folderId: string) => void;
  onDuplicate?: (flow: Flows, folderId: string) => void;
}> = ({
  agent,
  folderId,
  forceFluxbotIcon = false,
  onRename,
  onDelete,
  onShare,
  onOpenInNewTab,
  onCreateInterface,
  onAddToLibrary,
  onDuplicate,
}) => {
  const { activeFlowId, onFlowClick } = useAgentsSidebarContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isForm = isFormFlow(agent);
  const isFluxbot = forceFluxbotIcon || isFluxbotFlow(agent);

  return (
    <>
      <Item
        icon={
          isForm ? (
            <FileText size={16} />
          ) : isFluxbot ? (
            <ChatCircleText size={16} />
          ) : (
            <BoundingBox size={16} />
          )
        }
        label={agent.name}
        isActive={activeFlowId === agent.id}
        onClick={() => onFlowClick(agent, folderId)}
        onMenuClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onRename={onRename ? () => onRename(agent) : undefined}
        onShare={!isForm && onShare ? () => onShare(agent) : undefined}
        onOpenInNewTab={
          onOpenInNewTab ? () => onOpenInNewTab(agent) : undefined
        }
        onCreateInterface={
          !isForm && onCreateInterface
            ? () => onCreateInterface(agent, folderId)
            : undefined
        }
        onAddToLibrary={
          !isForm && onAddToLibrary
            ? () => onAddToLibrary(agent, folderId)
            : undefined
        }
        libraryLabel={
          agent.library ? "Edit Library" : "Add to Library"
        }
        onDuplicate={
          !isForm && onDuplicate
            ? () => onDuplicate(agent, folderId)
            : undefined
        }
        onDelete={onDelete ? () => onDelete(agent) : undefined}
      />
    </>
  );
};

const VectorMemoryItem: React.FC<{
  namespace: Namespace;
  onEdit: (ns: Namespace) => void;
  onDelete: (ns: Namespace) => void;
}> = ({ namespace, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Item
        icon={<HardDrives size={16} />}
        label={namespace.name}
        isNested
        onMenuClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onEdit={() => {
          setAnchorEl(null);
          onEdit(namespace);
        }}
        onDelete={() => {
          setAnchorEl(null);
          onDelete(namespace);
        }}
        showEdit={true}
      />
    </>
  );
};

export const AgentsSidebarBody: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ flowId?: string }>();
  const { setFolders, folders: storeFolders, user: authUser, setAuthUser } =
    useAuthStore((s) => s);
  const { searchTerm } = useExtendedSidebarContext();
  const { resetObjectUpdate, objects: objectStore } = useObjectStore((s) => s);
  const setNotification = useNotificationStore((s) => s.setNotification);

  const {
    folders,
    spaces,
    user: sidebarUser,
    isLoading: sidebarLoading,
    refresh: refreshSidebar,
  } = useSidebarData();

  const { data: privateNamespaces } = useQuery(vectorQueries.allNamespaces());

  const [activeFlowId, setActiveFlowId] = useState<string | undefined>();
  const [activeObjectId, setActiveObjectId] = useState<string | undefined>();
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [addUsersSpaceId, setAddUsersSpaceId] = useState<string | null>(null);
  const [copySpaceIdModalSpace, setCopySpaceIdModalSpace] = useState<{
    name: string;
    spaceId: string;
  } | null>(null);
  const [showUsersAddedModal, setShowUsersAddedModal] = useState(false);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [createFolderSpaceId, setCreateFolderSpaceId] = useState<
    string | undefined
  >(undefined);
  const [updateFolder, setUpdateFolder] = useState<Folder | null>(null);
  const [createAgentFolder, setCreateAgentFolder] = useState<{
    id: string;
    spaceId?: string;
  } | null>(null);

  const getUserObjects = useCallback(
    (): FluxObject[] => objectStore["user"] || authUser?.objects || [],
    [objectStore, authUser?.objects],
  );
  const getSpaceObjects = useCallback(
    (spaceId: string): FluxObject[] => objectStore[spaceId] || [],
    [objectStore],
  );
  const fluxObjects = getUserObjects();

  const {
    handlers,
    modals,
    setEditingObjectAndClose,
    setEditingVectorMemoryAndClose,
  } = useAgentsSidebarHandlers(activeFlowId);

  const sidebarData = useAgentsSidebarData(
    folders,
    spaces,
    searchTerm,
    fluxObjects,
    getSpaceObjects,
    privateNamespaces?.data ?? (authUser?.namespaces as Namespace[]) ?? [],
  );

  useEffect(() => {
    if (sidebarUser) setAuthUser(sidebarUser);
  }, [sidebarUser, setAuthUser]);

  useEffect(() => {
    if (folders.length > 0) setFolders(folders);
    else if (folders.length === 0 && storeFolders.length > 0) setFolders([]);
  }, [folders, setFolders, storeFolders.length]);

  useEffect(() => {
    const onRefresh = () => refreshSidebar();
    window.addEventListener("sidebar-refresh", onRefresh);
    return () => window.removeEventListener("sidebar-refresh", onRefresh);
  }, [refreshSidebar]);

  useEffect(() => {
    if (
      params.flowId &&
      (location.pathname.includes("/agent/") ||
        location.pathname.includes("/spaceAgent/") ||
        location.pathname.includes("/interface/"))
    ) {
      setActiveFlowId(params.flowId);
    } else {
      setActiveFlowId(undefined);
    }
  }, [params.flowId, location.pathname]);

  const handleFlowClick = useCallback(
    (flow: Flows, folderId?: string) => {
      setActiveFlowId(flow.id);
      setActiveObjectId(undefined);
      const isForm = isFormFlow(flow);
      let spaceId: string | undefined;
      if (folderId) {
        spaceId = parseSpaceIdFromFolderId(folderId);
        if (!spaceId && spaces.some((s) => s.id === folderId)) spaceId = folderId;
        if (!spaceId) {
          const folder =
            folders.find((f) => f.id === folderId) ||
            spaces.flatMap((s) => s.folder || []).find((f: Folder) => f.id === folderId);
          if (folder?.space) spaceId = folder.space;
        }
      }
      if (!spaceId)
        spaceId =
          flow.spaceId ||
          (typeof flow.space === "string" ? flow.space : undefined);
      const isSpaceFlow = !!spaceId;
      const route = isForm
        ? isSpaceFlow
          ? `/dashboard-beta/interface/${flow.id}${spaceId ? `?spaceId=${spaceId}` : ""}`
          : `/dashboard-beta/interface/${flow.id}`
        : isSpaceFlow
          ? `/dashboard-beta/spaceAgent/${flow.id}${spaceId ? `?spaceId=${spaceId}` : ""}`
          : `/dashboard-beta/agent/${flow.id}`;
      navigate(route);
    },
    [navigate, spaces, folders],
  );

  const handleObjectClick = useCallback((obj: FluxObject) => {
    setActiveObjectId(obj.id);
    setActiveFlowId(undefined);
  }, []);

  const handleFolderRename = useCallback((folder: Folder) => {
    setUpdateFolder(folder);
    setCreateFolderModalOpen(true);
  }, []);

  const handleAddFolder = useCallback((spaceId?: string) => {
    setCreateFolderSpaceId(spaceId);
    setCreateFolderModalOpen(true);
  }, []);

  const handleAddAgent = useCallback((folder: Folder) => {
    setCreateAgentFolder({
      id: folder.id,
      spaceId: folder.space || undefined,
    });
  }, []);

  const handleCreateFolderOrUpdate = useCallback(
    async (
      name: string,
      isEditing?: boolean,
      id?: string,
      _isSubfolder?: boolean,
      _isSubfolderEditing?: boolean,
    ) => {
      if (!name?.trim()) return;
      try {
        if (isEditing && id) {
          await FolderService.updateFolder(id, { name: name.trim() });
          refreshSidebar();
          setNotification({
            type: NotificationType.Success,
            message: "Folder renamed successfully",
          });
          setCreateFolderModalOpen(false);
          setUpdateFolder(null);
        } else if (createFolderSpaceId) {
          await FolderService.createFolderOnSpace({
            name: name.trim(),
            spaceId: createFolderSpaceId,
          });
          refreshSidebar();
          setNotification({
            type: NotificationType.Success,
            message: "Folder created successfully",
          });
          setCreateFolderModalOpen(false);
          setCreateFolderSpaceId(undefined);
        } else {
          await FolderService.createFolder({ name: name.trim() });
          refreshSidebar();
          setNotification({
            type: NotificationType.Success,
            message: "Folder created successfully",
          });
          setCreateFolderModalOpen(false);
          setCreateFolderSpaceId(undefined);
        }
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [createFolderSpaceId, setNotification, refreshSidebar],
  );

  const handleAddUsers = useCallback((spaceId: string) => {
    setAddUsersSpaceId(spaceId);
  }, []);

  const handleCopySpaceId = useCallback(
    (space: Space & { email?: string }) => {
      setCopySpaceIdModalSpace({
        name: space.name,
        spaceId: space.email ?? space.id,
      });
    },
    [],
  );

  if (sidebarLoading && folders.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const contextValue: AgentsSidebarContextValue = {
    isDarkMode,
    palette,
    activeFlowId,
    activeObjectId,
    onFlowClick: handleFlowClick,
    onObjectClick: handleObjectClick,
  };

  const {
    hasSearchTerm,
    allItems,
    privateFoldersToShow,
    privateItemsRaw,
    searchResult,
    allSpacesItemsRaw,
    getSpaceSectionData,
  } = sidebarData;

  const folderItemProps = {
    onFlowRename: handlers.handleFlowRename,
    onFlowDelete: handlers.handleFlowDelete,
    onFlowShare: handlers.handleFlowShare,
    onFlowOpenInNewTab: handlers.handleFlowOpenInNewTab,
    onFlowCreateInterface: handlers.handleCreateInterface,
    onFlowAddToLibrary: handlers.handleAddToLibrary,
    onFlowDuplicate: handlers.handleFlowDuplicate,
    onFolderRename: handleFolderRename,
    onFolderDelete: handlers.handleFolderDelete,
    onAddAgent: handleAddAgent,
  };

  return (
    <AgentsSidebarContext.Provider value={contextValue}>
      {showTemplatesModal && (
        <TemplateSystemModal onClose={() => setShowTemplatesModal(false)} />
      )}
      <ObjectModal
        show={!!modals.editingObject}
        onHide={async () => {
          setEditingObjectAndClose();
          resetObjectUpdate();
          refreshSidebar();
        }}
        spaceId={
          spaces.find((s) =>
            s.objects?.some((o) => o.id === modals.editingObject?.id),
          )?.id || "user"
        }
      />
      <VectorMemoryModal
        namespaceId={modals.editingVectorMemory?.id}
        spaceId={
          modals.editingVectorMemory
            ? spaces.find((s) =>
                s.namespaces?.some(
                  (n) => n.id === modals.editingVectorMemory?.id,
                ),
              )?.id
            : undefined
        }
        open={!!modals.editingVectorMemory}
        onOpenChange={async (state) => {
          if (!state.open) {
            setEditingVectorMemoryAndClose();
            refreshSidebar();
          }
        }}
      />
      {addUsersSpaceId && (
        <AddUsersModal
          spaceId={addUsersSpaceId}
          onClose={() => setAddUsersSpaceId(null)}
          getCompanySpace={async () => {
            refreshSidebar();
          }}
          setShowUsersAddedModal={setShowUsersAddedModal}
        />
      )}
      {copySpaceIdModalSpace && (
        <CopySpaceIDModal
          spaceName={copySpaceIdModalSpace.name}
          spaceId={copySpaceIdModalSpace.spaceId}
          onClose={() => setCopySpaceIdModalSpace(null)}
        />
      )}
      {showUsersAddedModal && (
        <UsersAddedModal onClose={() => setShowUsersAddedModal(false)} />
      )}
      <Box
        sx={{
          padding: "8px",
          borderBottom: `1px solid ${
            isDarkMode ? palette.gray[700] : palette.gray[200]
          }`,
          marginBottom: "8px",
        }}
      >
        <Item
          icon={<Stack size={16} />}
          label="Templates"
          onClick={() => setShowTemplatesModal(true)}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {hasSearchTerm && searchResult && allItems ? (
          <>
            {searchResult.filteredSubfolders.map((subfolder) => (
              <FolderItem
                key={subfolder.id}
                folder={subfolder}
                {...folderItemProps}
              />
            ))}
            {searchResult.filteredRootAgents.map((agent) => (
              <RootAgentItem
                key={agent.id}
                agent={agent}
                folderId="search-Agents"
                onRename={handlers.handleFlowRename}
                onDelete={handlers.handleFlowDelete}
                onShare={handlers.handleFlowShare}
                onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                onCreateInterface={handlers.handleCreateInterface}
                onAddToLibrary={handlers.handleAddToLibrary}
                onDuplicate={handlers.handleFlowDuplicate}
              />
            ))}
            {searchResult.filteredRootForms?.map((form) => (
              <RootAgentItem
                key={form.id}
                agent={form}
                folderId="search-Agents"
                onRename={handlers.handleFlowRename}
                onDelete={handlers.handleFlowDelete}
                onShare={handlers.handleFlowShare}
                onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                onCreateInterface={handlers.handleCreateInterface}
                onAddToLibrary={handlers.handleAddToLibrary}
                onDuplicate={handlers.handleFlowDuplicate}
              />
            ))}
            {allItems.fluxbots.map((flow) => (
              <RootAgentItem
                key={flow.id}
                agent={flow}
                folderId="search-Fluxbots"
                forceFluxbotIcon={true}
                onRename={handlers.handleFlowRename}
                onDelete={handlers.handleFlowDelete}
                onShare={handlers.handleFlowShare}
                onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                onCreateInterface={handlers.handleCreateInterface}
                onAddToLibrary={handlers.handleAddToLibrary}
                onDuplicate={handlers.handleFlowDuplicate}
              />
            ))}
            {allItems.objects.map((obj: FluxObject) => (
              <FluxBotItem
                key={obj.id}
                object={obj}
                onEdit={handlers.handleObjectEdit}
                onRename={handlers.handleObjectRename}
                onDelete={handlers.handleObjectDelete}
                isNested={false}
              />
            ))}
            {allItems.vectorMemory.length > 0 && (
              <FolderItem
                folder={createMainFolder("Vector Memory", allItems)}
                customItems={allItems.vectorMemory.map((ns) => (
                  <VectorMemoryItem
                    key={ns.id}
                    namespace={ns}
                    onEdit={handlers.handleVectorMemoryEdit}
                    onDelete={handlers.handleVectorMemoryDelete}
                  />
                ))}
              />
            )}
          </>
        ) : (
          <>
            <Section
              title="Private"
              hasBorder={spaces.length > 0}
              isAccordion={true}
              defaultExpanded={true}
              onAddFolder={() => handleAddFolder()}
            >
              {privateFoldersToShow.map((folder) => {
                const isShared =
                  folder.name?.trim().toLowerCase() === "shared";
                return (
                  <FolderItem
                    key={folder.id}
                    folder={folder}
                    {...folderItemProps}
                    onFolderRename={
                      isShared ? undefined : handleFolderRename
                    }
                    onFolderDelete={
                      isShared ? undefined : handlers.handleFolderDelete
                    }
                  />
                );
              })}
              <FolderItem
                folder={createMainFolder("Vector Memory", privateItemsRaw)}
                customItems={privateItemsRaw.vectorMemory.map((ns) => (
                  <VectorMemoryItem
                    key={ns.id}
                    namespace={ns}
                    onEdit={handlers.handleVectorMemoryEdit}
                    onDelete={handlers.handleVectorMemoryDelete}
                  />
                ))}
              />
            </Section>

            {[...spaces]
              .map((space, index) => ({
                space,
                spaceItems: allSpacesItemsRaw[index],
              }))
              .sort((a, b) =>
                (a.space.disabled ? 1 : 0) - (b.space.disabled ? 1 : 0),
              )
              .map(({ space, spaceItems }, index, sorted) => {
                const {
                  agentsStructure,
                  sharedFolderToShow,
                  vectorMemoryFolder,
                } = getSpaceSectionData(space, spaceItems);
                return (
                  <Section
                    key={space.id}
                    title={space.name}
                    hasBorder={index < sorted.length - 1}
                    isAccordion={true}
                    defaultExpanded={false}
                    isDisabled={!!space.disabled}
                    onAddFolder={() => handleAddFolder(space.id)}
                    onEditSpace={() => handlers.handleEditSpace(space.id)}
                    onArchive={() => handlers.handleArchiveSpace(space.id)}
                    onAddUsers={() => handleAddUsers(space.id)}
                    onCopySpaceId={() => handleCopySpaceId(space)}
                  >
                    {agentsStructure.subfolders.map((subfolder) => (
                      <FolderItem
                        key={subfolder.id}
                        folder={subfolder}
                        {...folderItemProps}
                      />
                    ))}
                    <FolderItem
                      key={sharedFolderToShow.id}
                      folder={sharedFolderToShow}
                      {...folderItemProps}
                      onFolderRename={undefined}
                      onFolderDelete={undefined}
                    />
                    <FolderItem
                      folder={vectorMemoryFolder}
                      customItems={spaceItems.vectorMemory.map((ns) => (
                        <VectorMemoryItem
                          key={ns.id}
                          namespace={ns}
                          onEdit={handlers.handleVectorMemoryEdit}
                          onDelete={handlers.handleVectorMemoryDelete}
                        />
                      ))}
                    />
                    {agentsStructure.rootAgents.map((agent) => (
                      <RootAgentItem
                        key={agent.id}
                        agent={agent}
                        folderId={`${space.id}-Agents`}
                        onRename={handlers.handleFlowRename}
                        onDelete={handlers.handleFlowDelete}
                        onShare={handlers.handleFlowShare}
                        onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                        onCreateInterface={handlers.handleCreateInterface}
                        onAddToLibrary={handlers.handleAddToLibrary}
                        onDuplicate={handlers.handleFlowDuplicate}
                      />
                    ))}
                    {agentsStructure.rootForms?.map((form) => (
                      <RootAgentItem
                        key={form.id}
                        agent={form}
                        folderId={`${space.id}-Agents`}
                        onRename={handlers.handleFlowRename}
                        onDelete={handlers.handleFlowDelete}
                        onShare={handlers.handleFlowShare}
                        onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                        onCreateInterface={handlers.handleCreateInterface}
                        onAddToLibrary={handlers.handleAddToLibrary}
                        onDuplicate={handlers.handleFlowDuplicate}
                      />
                    ))}
                    {spaceItems.fluxbots.map((flow) => (
                      <RootAgentItem
                        key={flow.id}
                        agent={flow}
                        folderId={`${space.id}-Fluxbots`}
                        forceFluxbotIcon={true}
                        onRename={handlers.handleFlowRename}
                        onDelete={handlers.handleFlowDelete}
                        onShare={handlers.handleFlowShare}
                        onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                        onCreateInterface={handlers.handleCreateInterface}
                        onAddToLibrary={handlers.handleAddToLibrary}
                        onDuplicate={handlers.handleFlowDuplicate}
                      />
                    ))}
                    {spaceItems.objects.map((obj: FluxObject) => (
                      <FluxBotItem
                        key={obj.id}
                        object={obj}
                        onEdit={handlers.handleObjectEdit}
                        onRename={handlers.handleObjectRename}
                        onDelete={handlers.handleObjectDelete}
                        isNested={false}
                      />
                    ))}
                  </Section>
                );
              })}
          </>
        )}
      </Box>

      <CreateFormModal
        flow={modals.createInterfaceFlow?.flow}
        isCreateFormLoading={modals.isCreateFormLoading}
        show={!!modals.createInterfaceFlow}
        onCreateForm={handlers.handleCreateForm}
        onHide={() => modals.setCreateInterfaceFlow(null)}
      />

      <AddToLibraryModal
        flow={modals.libraryFlow?.flow}
        folderId={modals.libraryFlow?.folderId}
        show={!!modals.libraryFlow}
        onHide={() => modals.setLibraryFlow(null)}
        isEditMode={!!modals.libraryFlow?.flow?.library}
        onAddToLibrary={() => {
          setNotification({
            type: NotificationType.Success,
            message: modals.libraryFlow?.flow?.library
              ? "Library updated successfully"
              : "Agent added to library successfully",
          });
          modals.setLibraryFlow(null);
          refreshSidebar();
        }}
      />

      <CreateFolderModal
        show={createFolderModalOpen}
        data={
          updateFolder
            ? {
                isSubfolder: false,
                isEditing: true,
                folder: { id: updateFolder.id, name: updateFolder.name },
              }
            : {
                isSubfolder: false,
                isEditing: false,
                folder: { id: "", name: "" },
              }
        }
        onHide={() => {
          setCreateFolderModalOpen(false);
          setCreateFolderSpaceId(undefined);
          setUpdateFolder(null);
        }}
        onCreateFolderOrUpdate={handleCreateFolderOrUpdate}
        theme={authUser?.theme}
      />

      {createAgentFolder && (
        <CreateItemModal
          show={true}
          onHide={() => setCreateAgentFolder(null)}
          type="agent"
          initialFolderId={createAgentFolder.id}
          initialSpaceId={createAgentFolder.spaceId}
        />
      )}
    </AgentsSidebarContext.Provider>
  );
};
